import { faEdit, faPlay, faPlus, faSquare, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import BGTaskScheduleService from '../../service/BGTaskScheduleService'
import BGTaskService from '../../service/BGTaskService'
import { expandRowFunction, formatBooleanColumn } from '../../utils/DatatableUtil'
import { getDataField } from '../../utils/DialogUtil'
import GatewayType from '../GatewayType'
import RecentBGTasksTableComponent from './RecentBGTasksTableComponent'
import TokenProfileService from '../../../gateway/service/TokenProfileService'
import { FormDataControl } from '../../components/FormFields'


export const BGSchedule: GatewayType = (props: any) => {
  const { t } = useTranslation()
  const [bgSchedules, setSchedules] = useState([] as any[])
  const [bgTaskScheduleDefinition, setBGTaskScheduleDefinition] = useState([] as any[])
  const [bgTaskFieldConfigs, setBGTaskFieldConfigs] = useState([] as any[])
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(true)
  const { userPermissions } = useContext(AppContext)

  useEffect(() => {
    async function loadData() {
      const schedules: any[] = await BGTaskScheduleService.getSchedules(setSchedules)
      const definitions = await BGTaskScheduleService.getDefinitions()
      setBGTaskScheduleDefinition(definitions)

      if (schedules.filter((schedule: any) => schedule.type === t('bgTaskSchedule.rppAPISchedule')).length > 0) {
        await BGTaskScheduleService.getAllFieldConfigs(setBGTaskFieldConfigs)
      }

    }

    loadData().finally(() => setLoading(false))
  }, [t])

  async function callAddFieldConfigurationAfterFetch(row: any) {
    const tokenProfiles = await TokenProfileService.getConfigurableTokenProfiles().then((profiles) =>
      profiles.filter((profile: any) => !profile.unique && profile.reversible && profile.generic)
    )

    addFieldConfiguration(t, editDialogPropsSetter, setBGTaskFieldConfigs, tokenProfiles, row)
  }

  async function editFieldConfigurationAfterFetch(row: any) {
    const tokenProfiles = await TokenProfileService.getConfigurableTokenProfiles().then((profiles) =>
      profiles.filter((profile: any) => !profile.unique && profile.reversible && profile.generic)
    )
    editFieldConfiguration(t, editDialogPropsSetter, setBGTaskFieldConfigs, tokenProfiles, row)
  }

  const columnsSchedules = [
    {
      text: t('name.text'),
      dataField: 'name',
      sort: true,
      classes: 'position-relative',
      formatter: (cellData: any, row: any) => {
        return AuthService.hasPermission(userPermissions, Authorization.bgSchedule.edit) ? (
          <ProtectedButton
            variant='link'
            className='text-decoration-none pl-0 stretched-link text-left'
            tooltip={t('edit')}
            onClick={() => {
              editScheduleDialog(t, editDialogPropsSetter, setSchedules, row)
            }}>
            <FontAwesomeIcon icon={faEdit} /> {cellData}
          </ProtectedButton>
        ) : (
          cellData
        )
      },
    },
    {
      text: t('Type'),
      dataField: 'type',
      sort: true,
    },
    {
      text: t('active'),
      dataField: 'active',
      formatter: (cell: any) => formatBooleanColumn(cell),
      sort: true,
    },
    {
      text: t('cronPatterns.text'),
      dataField: 'cronPatterns',
      sort: true,
    },
    {
      text: t('bgTaskSchedule.lastRun'),
      dataField: 'lastScheduled',
      sort: true,
    },
    {
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      classes: 'text-center',
      formatter: (cellData: any, row: any) => {
        return (
          <>
            <ProtectedButton
              permissions={Authorization.bgSchedule.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('run')}
              onClick={() => runScheduleDialog(t, confirmDialogPropsSetter, row, reload, setReload)}>
              <FontAwesomeIcon icon={faPlay} className='text-primary' />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.bgSchedule.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('cancel')}
              onClick={() => cancelScheduleDialog(row)}>
              <FontAwesomeIcon icon={faSquare} className='text-danger' />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.bgSchedule.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteScheduleDialog(row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
            <ProtectedButton
              permissions={Authorization.adapter.edit}
              shouldRender={t('bgTaskSchedule.rppAPISchedule') === row.type}
              variant='link'
              className='text-decoration-none pr-2'
              tooltip={t('bgTaskSchedule.addFieldConfig')}
              onClick={() => callAddFieldConfigurationAfterFetch(row)}>
              <FontAwesomeIcon icon={faPlus} />
            </ProtectedButton>
          </>
        )
      },
    },
  ]

  const expandRow = expandRowFunction(renderer, () => { })

  function renderer(row: any, _rowIndex: any) {

    if (row.type === t('bgTaskSchedule.rppAPISchedule') && bgTaskFieldConfigs.filter((config: any) => config.bgiSchedule === row.id).length > 0) {

      const data = bgTaskFieldConfigs.filter((config: any) => config.bgiSchedule === row.id)
      const columns: any[] = [
        {
          text: t('restprotection.jsonPath'),
          dataField: 'jsonPath',
          sort: true,
        },
        {
          text: t('restprotection.method'),
          dataField: 'method',
          formatter: (cell: any, row: any) => {
            if (cell === t('testdata.tokenizerConfig.methods.tokenization')) {
              return `${cell} (${row.tokenProfileName})`
            } else {
              return cell
            }
          },
        },
        {
          dataField: 'df1',
          isDummyField: true,
          text: t('action'),
          headerAlign: 'center',
          formatter: (cellData: any, row: any) => {
            return (
              <>
                <ProtectedButton
                  permissions={Authorization.adapter.edit}
                  variant='link'
                  className='text-link text-decoration-none'
                  tooltip={t('edit')}
                  onClick={() => editFieldConfigurationAfterFetch(row)}>
                  <FontAwesomeIcon icon={faEdit} />
                </ProtectedButton>
                <ProtectedButton
                  permissions={Authorization.adapter.edit}
                  variant='link'
                  className='text-danger text-link text-decoration-none'
                  tooltip={t('delete')}
                  onClick={() => deleteFieldConfiguration(t, confirmDialogPropsSetter, setBGTaskFieldConfigs, row)}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </ProtectedButton>
              </>
            )
          },
        },
      ]
      if (!data.length) {
        return <React.Fragment key={row.id} />
      }
      return (
        <React.Fragment key={row.id}>
          <DataTable
            data={data}
            columns={columns}
            searchable={false}
            striped={false}
            hover={false}
            condensed={true}
            noDataIndication={() => <span className='text-center'>{t('testdata.tokenizerConfig.noConfigFound')}</span>}
          />
          <RecentBGTasksTableComponent row={row} reload={reload} />
        </React.Fragment>
      )
    }

    return <RecentBGTasksTableComponent row={row} reload={reload} />
  }

  const defaultSorted = [
    {
      dataField: 'username',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={bgSchedules}
        columns={columnsSchedules}
        caption={t('Jobs')}
        helpLink={t('bgTaskSchedule.helpLink')}
        defaultSorted={defaultSorted}
        expandRow={expandRow}
        captionButtons={[
          {
            onClick: () => createScheduleDialog(bgTaskScheduleDefinition),
            text: t('new'),
            permissions: Authorization.bgSchedule.edit,
          },
        ]}
      />
    </Loading>
  )

  function createScheduleDialog(definitions: any[]) {
    if (!definitions || definitions.length === 0) {
      createNoDefinitionFoundDialog()
      return
    }

    definitions.sort((a, b) => a.label.localeCompare(b.label))
    const editDialogProps: EditDialogProps = {
      show: true,
      header: t('bgTaskSchedule.createHeader'),
      onSubmit: BGTaskScheduleService.createSchedule,
      onSubmitExtraArgs: [t, setSchedules],
      data: (custom: string[]) => [
        {
          id: 'type',
          type: 'select',
          options: definitions.map((definition) => {
            return { label: definition.name, value: definition.type }
          }),
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
          value: custom[0],
          label: t('type.text'),
          required: true,
        },
        ...getDefinitions(definitions, custom),
      ],
      labelGridSize: 4,
      initCustom: [definitions[0].type],
    }
    editDialogPropsSetter(editDialogProps)
  }

  function createNoDefinitionFoundDialog() {
    const confirmDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('bgTaskSchedule.errorTitle'),
      body: t('bgTaskSchedule.noTaskDefinitionFoundError'),
      showCancelButton: false,
      submitText: 'Ok',
    }
    confirmDialogPropsSetter(confirmDialogProps)
  }

  function getDefinitions(definitions: any[], custom: string[]) {
    return definitions
      .map((definition) => {
        definition.attributes.forEach((attribute: any) => (attribute.profile = definition.type))
        return definition.attributes
      })
      .reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
      .filter((attribute: any) => attribute.name !== 'type' && attribute.profile === custom[0])
      .map((attribute: any) => getDataField(attribute, attribute.profile))
  }

  function deleteScheduleDialog(row: any) {
    const deleteDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('bgTaskSchedule.deleteHeader'),
      body: t('bgTaskSchedule.deleteBody', { name: row.name }),
      submitText: t('yes'),
      onSubmit: BGTaskScheduleService.deleteSchedule,
      onSubmitExtraArgs: [row, t, setSchedules],
      cancelText: t('no'),
    }
    confirmDialogPropsSetter(deleteDialogProps)
  }

  function cancelScheduleDialog(row: any) {
    const cancelDialogProps: ConfirmDialogProps = {
      show: true,
      header: t('bgTaskSchedule.cancelHeader'),
      body: t('bgTaskSchedule.cancelBody', { name: row.name }),
      submitText: t('yes'),
      onSubmit: BGTaskService.cancel,
      onSubmitExtraArgs: [row.id, reload, setReload],
      cancelText: t('no'),
    }
    confirmDialogPropsSetter(cancelDialogProps)
  }
}

async function editScheduleDialog(t: TFunction, editDialogPropsSetter: any, setSchedules: React.Dispatch<any>, row: any) {
  const bgSchedule = await BGTaskScheduleService.getByID(row.id)
  const definition = await BGTaskScheduleService.getDefinitionByType(bgSchedule.type)
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('bgTaskSchedule.editHeader'),
    onSubmit: BGTaskScheduleService.updateSchedule,
    onSubmitExtraArgs: [setSchedules, t],
    data: getEditData(bgSchedule, definition),
    initCustom: [bgSchedule.type],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function getEditData(schedule: any, definition: any): any[] {
  const data = definition.attributes
    .map((attribute: any) => {
      attribute.value = schedule[attribute.name]
      if (attribute.name === 'type') {
        attribute.plaintext = true
        attribute.readOnly = true
      }
      return getDataField(attribute)
    })
    .filter((val: any) => Object.keys(val).length > 0)
  data.push({
    id: 'id',
    value: schedule.id,
    type: 'hidden',
    render: false,
  })

  return data
}

async function runScheduleDialog(t: TFunction, confirmDialogPropsSetter: any, row: any, reload: boolean, setReload: React.Dispatch<any>) {
  const runDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('bgTaskSchedule.runNowHeader'),
    body: t('bgTaskSchedule.runNowBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: BGTaskService.createTask,
    onSubmitExtraArgs: [row.id, reload, setReload],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(runDialogProps)
}

function addFieldConfiguration(t: TFunction, editDialogPropsSetter: any, setBGTaskFieldConfigs: React.Dispatch<any>, tokenProfiles: any[], row: any) {
  const encryptionMethods = [t('testdata.tokenizerConfig.methods.encryption'), t('testdata.tokenizerConfig.methods.tokenization')]

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.createFieldHeader'),
    onSubmit: BGTaskScheduleService.createScheduleConfig,
    onSubmitExtraArgs: [t, setBGTaskFieldConfigs],
    data: (custom: string[]) => [
      {
        id: 'bgiSchedule',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'jsonPath',
        type: 'text',
        label: t('restprotection.jsonPath'),
        placeholder: t('restprotection.jsonPathPlaceholder'),
        required: true,
      },
      {
        id: 'method',
        type: 'select',
        render: true,
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('restprotection.method'),
        required: true,
      },
      ...getFinalField(tokenProfiles, custom[0], t),
    ],
    initCustom: [t('testdata.tokenizerConfig.methods.tokenization')],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function getFinalField(tokenProfiles: any[], custom: string, t: TFunction, existingValue?: any): FormDataControl[] {
  if (custom === t('testdata.tokenizerConfig.methods.tokenization')) {
    return [
      {
        id: 'tokenProfileName',
        type: 'select',
        options: tokenProfiles.map((profile) => profile.name),
        value: existingValue ? existingValue : tokenProfiles[0].name,
        label: t('testdata.tokenizerConfig.tokenProfile'),
        required: true,
      },
    ]
  }
  return []
}

function deleteFieldConfiguration(t: TFunction, confirmDialogPropsSetter: React.Dispatch<any>, setBGTaskFieldConfigs: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('restprotection.deleteFieldHeader'),
    body: t('restprotection.deleteFieldBody', { path: row.jsonPath }),
    submitText: t('yes'),
    onSubmit: BGTaskScheduleService.deleteScheduleConfig,
    onSubmitExtraArgs: [row, t, setBGTaskFieldConfigs],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function editFieldConfiguration(
  t: TFunction,
  editDialogPropsSetter: any,
  setBGTaskFieldConfigs: React.Dispatch<any>,
  tokenProfiles: any[],
  row: any
) {
  const encryptionMethods = [t('testdata.tokenizerConfig.methods.encryption'), t('testdata.tokenizerConfig.methods.tokenization')]

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('restprotection.editFieldHeader'),
    onSubmit: BGTaskScheduleService.updateScheduleConfig,
    onSubmitExtraArgs: [t, setBGTaskFieldConfigs],
    data: (custom: string[]) => [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
      },
      {
        id: 'bgiSchedule',
        type: 'hidden',
        value: row.bgiSchedule,
      },
      {
        id: 'jsonPath',
        type: 'text',
        label: t('restprotection.jsonPath'),
        placeholder: t('restprotection.jsonPathPlaceholder'),
        value: row.jsonPath,
        required: true,
      },
      {
        id: 'method',
        type: 'select',
        render: true,
        options: encryptionMethods,
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('restprotection.method'),
        required: true,
      },
      ...getFinalField(tokenProfiles, custom[0], t, row.tokenProfileName),
    ],
    initCustom: [row.method],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

export default BGSchedule
